import type React from 'react';
import type { DrawColor } from '../VideoCanvasOverlay';
import { DRAW_COLORS, SPEEDS, type DrawingTool, type EditorTool, type Speed } from './editorControlValues';
import { EBTN_ICON, EBTN_SEGMENTED, EBTN_SWATCH_TARGET } from './editorButtonStyles';
import { usePhoneLayout } from './usePhoneLayout';

/**
 * The editor's playback and drawing controls, lifted out of
 * ClipEditorModal so the live room's match player uses the same ones: the
 * drawing toolbar, the speed and mute cluster, the pen colours and the
 * speeds. Moved unchanged; the toolbar gained optional `tools` (arrow and
 * circle, which only the room offers), optional undo and redo, and an
 * optional Clear All.
 */

const TOOL_LABEL: Record<Exclude<DrawingTool, 'none'>, string> = {
  brush: 'Draw on video', eraser: 'Erase drawing', arrow: 'Draw an arrow', circle: 'Draw a circle',
};
const TOOL_ICON: Record<Exclude<DrawingTool, 'none'>, React.ReactNode> = {
  brush: (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M7 14c-1.66 0-3 1.34-3 3 0 1.31-1.16 2-2 2 .92 1.22 2.49 2 4 2 2.21 0 4-1.79 4-4 0-1.66-1.34-3-3-3zm13.71-9.37-1.34-1.34a1 1 0 0 0-1.41 0L9 12.25 11.75 15l8.96-8.96a1 1 0 0 0 0-1.41z" />
    </svg>
  ),
  eraser: (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M15.14 3c-.51 0-1.02.2-1.41.59L2.59 14.73c-.78.77-.78 2.04 0 2.83L5.03 20H20v-2h-8.56l7.97-7.97c.78-.78.78-2.05 0-2.83l-2.85-2.85A2 2 0 0015.14 3z" />
    </svg>
  ),
  arrow: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <path d="M5 19 19 5M10 5h9v9" />
    </svg>
  ),
  circle: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="8" />
    </svg>
  ),
};

/**
 * The drawing tools.
 *
 * One instance serves both layouts: inside a panel when there is a single
 * source, and once beneath both panels when there are two. The tool, the
 * colour and the history buttons are the parent's; which canvas a stroke lands
 * on is still decided by the pointer, not here.
 *
 * `children` is the per-panel utility cluster (speed, mute) that the
 * single-source layout keeps in this row. Side by side passes none — those
 * controls belong to a panel and move into each transport.
 */
export function DrawToolbar<T extends DrawingTool = EditorTool>({
  drawingTool, setDrawingTool, drawColor, setDrawColor,
  canAnnotate, undoDepth = 0, redoDepth = 0, onUndo, onRedo, fullWidth = false, children,
  tools = ['brush', 'eraser'] as Exclude<T, 'none'>[], onClear,
}: {
  drawingTool: T;
  setDrawingTool: React.Dispatch<React.SetStateAction<T>>;
  drawColor: DrawColor;
  setDrawColor: (c: DrawColor) => void;
  canAnnotate: boolean;
  undoDepth?: number;
  redoDepth?: number;
  /** Undo and redo show only when given (the editor). */
  onUndo?: () => void;
  onRedo?: () => void;
  fullWidth?: boolean;
  children?: React.ReactNode;
  /** The tools offered, in order. The editor's default is brush and eraser. */
  tools?: Exclude<T, 'none'>[];
  /** Clear All, shown when given (the live room). */
  onClear?: () => void;
}) {
  const canAnnotateEffective = canAnnotate;
  /* On a phone the row wraps instead of running off the edge: the pen tools,
     the swatches and speed and mute do not fit one 375 line. */
  const isMobile = usePhoneLayout().phone;
  return (
    <div
      /* Paired: a pill that shrinks to its content and sits centred under the
         pair, on a surface one step above the panels so it reads as a control
         rather than as an empty band. Single source keeps the full-width chip
         it has always had, because it also carries speed and mute. */
      className={`flex items-center flex-shrink-0 ${fullWidth ? 'justify-center' : 'w-full justify-between'} phone:flex-wrap phone:gap-y-1`}
      style={{
        background: fullWidth ? 'var(--editor-surface-raised)' : 'var(--editor-surface)',
        borderRadius: fullWidth ? '100px' : '8px',
        padding: isMobile ? '4px 12px' : '0 12px',
        height: isMobile ? 'auto' : '36px',
        minHeight: isMobile ? '36px' : undefined,
        margin: isMobile ? 0 : '4px 0',
      }}
    >
        <div className="flex items-center gap-1">
          {/* Draw label */}
          {/* Brush and eraser are mutually exclusive, so they are one control
              rather than two loose glyphs. Track 32 inside a 36 row, so the row
              does not grow. Both still toggle back to 'none' on a second press,
              which is why the group can be all-unchecked. */}
          <div
            role="radiogroup"
            aria-label="Drawing tool"
            className={EBTN_SEGMENTED.track}
          >
            {tools.map((tool) => (
              <button
                key={tool}
                type="button"
                role="radio"
                aria-checked={drawingTool === tool}
                onClick={() => setDrawingTool((t) => (t === tool ? 'none' as T : tool))}
                disabled={!canAnnotateEffective}
                className={drawingTool === tool ? EBTN_SEGMENTED.iconItemSelected : EBTN_SEGMENTED.iconItem}
                title={canAnnotateEffective ? TOOL_LABEL[tool] : 'Pause video to annotate'}
                aria-label={TOOL_LABEL[tool]}
              >
                {TOOL_ICON[tool]}
              </button>
            ))}
          </div>

          {/* Undo and redo, where clear-all used to be. Repeated undo reaches
              the same empty canvas without a single irreversible press, so the
              destructive control is gone rather than relocated.

              mx-1.5, not mx-1: at mx-1 the eraser's 44 target and undo's 44
              target overlapped by 2px, and the overlap belonged to undo. */}
          {(onUndo || onRedo) && (<>
          <div className="w-px h-4 mx-1.5" style={{ background: 'var(--editor-border)' }} />
          <button
            type="button"
            onClick={() => onUndo?.()}
            disabled={undoDepth === 0}
            className={EBTN_ICON}
            title="Undo"
            aria-label="Undo"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => onRedo?.()}
            disabled={redoDepth === 0}
            className={EBTN_ICON}
            title="Redo"
            aria-label="Redo"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.06-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z" />
            </svg>
          </button>
          </>)}
          {onClear && (<>
            <div className="w-px h-4 mx-1.5" style={{ background: 'var(--editor-border)' }} />
            <button type="button" onClick={onClear} className={EBTN_ICON} title="Clear all" aria-label="Clear all">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
              </svg>
            </button>
          </>)}

          {/* Color swatches — only visible when brush is active */}
          {(drawingTool === 'brush' || drawingTool === 'arrow' || drawingTool === 'circle') && canAnnotateEffective && (
            <>
              <div className="w-px h-4 mx-1" style={{ background: 'var(--editor-border)' }} />
              {/* gap-3, because EBTN_SWATCH_TARGET reaches 6px past each
                  swatch and a 12px gap is exactly what keeps one swatch's
                  target out of its neighbour's. */}
              <div role="radiogroup" aria-label="Pen colour" className="flex items-center gap-3">
                {DRAW_COLORS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    role="radio"
                    aria-checked={drawColor === c.value}
                    aria-label={c.label}
                    onClick={() => setDrawColor(c.value)}
                    className={`${EBTN_SWATCH_TARGET} w-5 h-5 rounded-full border-2 transition-transform cursor-pointer ${
                      drawColor === c.value
                        ? 'border-white scale-110'
                        : 'border-[var(--editor-border)] hover:border-[var(--editor-text-muted)]'
                    }`}
                    style={{ backgroundColor: c.hex }}
                    title={c.label}
                  />
                ))}
              </div>
            </>
          )}
      </div>
      {children}
    </div>
  );
}


/** Speed and mute. Belongs to a panel, never to the shared drawing tools. */
export function PanelUtilities({ showVideoControls, speed, onSpeedChange, isMuted, onToggleMute }: {
  showVideoControls: boolean;
  speed: Speed;
  onSpeedChange: (s: Speed) => void;
  isMuted: boolean;
  onToggleMute: () => void;
}) {
  const phone = usePhoneLayout().phone;
  return (
        <div className="flex items-center gap-2">
          {showVideoControls && (
            <div
              role="radiogroup"
              aria-label="Playback speed"
              className={EBTN_SEGMENTED.track}
            >
              {SPEEDS.map((s) => (
                <button
                  key={s}
                  type="button"
                  role="radio"
                  aria-checked={speed === s}
                  onClick={() => onSpeedChange(s)}
                  className={speed === s ? EBTN_SEGMENTED.itemSelected : EBTN_SEGMENTED.item}
                >
                  {s}×
                </button>
              ))}
            </div>
          )}
          {/* Muted is red, and the glyph carries waves when it is not, so the
              on state is legible on its own rather than only against the off
              one. The colour is inline because it has to beat the recipe's,
              and two arbitrary-value classes of the same property resolve by
              emission order rather than by class order. */}
          <button
            type="button"
            onClick={onToggleMute}
            className={EBTN_ICON}
            /* Phone: grey when muted, white when not. Red read as an error. */
            style={phone
              ? { color: isMuted ? 'var(--editor-text-muted)' : 'var(--editor-text)' }
              : (isMuted ? { color: 'var(--editor-danger)' } : undefined)}
            aria-pressed={isMuted}
            title={isMuted ? 'Unmute audio' : 'Mute audio'}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
              </svg>
            )}
          </button>
        </div>
  );
}

